import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Ruler } from '@/components/icons';
import { useQuery } from '@tanstack/react-query';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { getGrnLabels, type GrnLabelsItem } from '@/api/grn';
import { Button } from '@/components/ui/Button';
import {
  buildCalibrationHtml,
  buildLabelPrintHtml,
  escapeHtml,
  openPrintWindow,
  THERMAL_LABEL_PRESETS,
} from '@/lib/labelPrint';
import { LabelsPerRowToggle } from '@/components/ui/LabelsPerRowToggle';

interface Props {
  open: boolean;
  grnId: number | null;
  grnNo?: string;
  onClose: () => void;
}

type LabelSize = typeof THERMAL_LABEL_PRESETS[number]['key'] | 'custom';
const SIZES = THERMAL_LABEL_PRESETS;

interface PreparedSerial {
  serialNo: string;
  itemName: string;
  itemCode: string;
  category: string;
  unit: string;
  barcodeUrl: string;
  qrUrl: string;
}

function genBarcodeSvgUrl(code: string): string {
  try {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(svg, code, {
      format: 'CODE128',
      width: 1.8,
      height: 40,
      displayValue: false,
      margin: 4,
      background: '#ffffff',
      lineColor: '#000000',
    });
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg));
  } catch {
    return '';
  }
}

export function GrnLabelsModal({ open, grnId, grnNo, onClose }: Props) {
  const [labelSize, setLabelSize] = useState<LabelSize>('60x40');
  const [perRow, setPerRow] = useState<1 | 2>(1);
  const [customW, setCustomW] = useState('60');
  const [customH, setCustomH] = useState('40');
  // Fixed safe-zone padding (in mm) — thermal printers have a ~3mm dead
  // zone on the left where the printhead can't reach. Left kept at 3 so
  // the first character doesn't clip; Bottom kept at 2 so the barcode
  // floats a bit above the sticker's bottom edge instead of flush.
  const offsetLeft = '3';
  const offsetTop = '2';
  const offsetRight = '3';
  const offsetBottom = '2';
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [previewSerial, setPreviewSerial] = useState<PreparedSerial | null>(null);

  const query = useQuery({
    queryKey: ['grn-labels', grnId],
    queryFn: () => getGrnLabels(grnId!),
    enabled: open && grnId !== null,
    staleTime: 30_000,
  });

  const items = query.data?.items ?? [];

  // Initialize selection to all items whenever data arrives.
  useEffect(() => {
    if (open && query.data) {
      setSelectedItemIds(new Set(query.data.items.map((i) => i.grnItemId)));
    }
  }, [open, query.data]);

  // Build preview for the first serial of the first selected item.
  useEffect(() => {
    if (!open || items.length === 0) {
      setPreviewSerial(null);
      return;
    }
    const firstSelected = items.find((i) => selectedItemIds.has(i.grnItemId));
    const s = firstSelected?.serials[0];
    if (!s || !firstSelected) {
      setPreviewSerial(null);
      return;
    }
    (async () => {
      const barcodeUrl = genBarcodeSvgUrl(s.serialNo);
      const qrUrl = await QRCode.toDataURL(s.serialNo, {
        width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' },
      });
      setPreviewSerial({
        serialNo: s.serialNo,
        itemName: firstSelected.item.name,
        itemCode: firstSelected.item.code,
        category: firstSelected.item.category ?? '',
        unit: firstSelected.unit,
        barcodeUrl,
        qrUrl,
      });
    })();
  }, [open, items, selectedItemIds]);

  const totalSerials = useMemo(() => {
    return items
      .filter((i) => selectedItemIds.has(i.grnItemId))
      .reduce((acc, i) => acc + i.serials.length, 0);
  }, [items, selectedItemIds]);

  function toggleItem(id: number) {
    setSelectedItemIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAll() {
    setSelectedItemIds(new Set(items.map((i) => i.grnItemId)));
  }
  function selectNone() {
    setSelectedItemIds(new Set());
  }

  const esc = escapeHtml;

  function singleLabelHtml(
    fw: number, fh: number,
    serialNo: string, item: GrnLabelsItem,
    barcodeUrl: string, qrUrl: string, qty: number,
  ): string {
    // Everything scales linearly with the label's smallest dimension against
    // a 60×40 mm reference — so a 40×30 label shrinks to ~75%, an 80×50 grows
    // to ~125%, and every internal element keeps its visual proportions.
    const s = Math.min(fw / 60, fh / 40);
    const baseFs = Math.max(3.5, 6 * s);
    const nameLen = item.item.name.length;
    // Name font also shrinks the longer the text is so it never spills out.
    const nameFs =
      nameLen > 40 ? Math.max(3.5, (baseFs - 1) * (40 / nameLen)) :
      nameLen > 24 ? Math.max(3.5, baseFs) :
                     baseFs + 1;
    const codeLen = serialNo.length;
    const codeFs =
      codeLen > 22 ? baseFs + 1 :
      codeLen > 14 ? baseFs + 2 :
                     baseFs + 4;
    // Physical dimensions scale with whichever axis is smaller so a portrait
    // sticker's QR fits horizontally, and a landscape's QR fits vertically.
    const shortSide = Math.min(fw, fh);
    const qrSide = shortSide * 0.40;   // 40% of the short side
    const bcHeight = shortSide * 0.28; // 28% of the short side
    const qrCaption = serialNo.length > 14 ? serialNo.slice(0, 13) + '…' : serialNo;
    return `
<div class="cell">
  <div class="top-row">
    <div class="info">
      <div class="item-name" style="font-size:${nameFs.toFixed(2)}pt">${esc(item.item.name)}</div>
      ${item.item.category ? `<div class="item-cat" style="font-size:${baseFs.toFixed(2)}pt">${esc(item.item.category)}</div>` : ''}
      <div class="serial-no" style="font-size:${codeFs.toFixed(2)}pt">${esc(serialNo)}</div>
      ${qty > 1 ? `<div class="box-qty" style="font-size:${(baseFs + 3).toFixed(2)}pt;font-weight:800">Qty: ${qty} Pcs</div>` : ''}
    </div>
    <div class="qr-block">
      <img src="${qrUrl}" style="width:${qrSide.toFixed(2)}mm;height:${qrSide.toFixed(2)}mm" />
      <div class="qr-text" style="font-size:${Math.max(2.5, baseFs - 2).toFixed(2)}pt;max-width:${qrSide.toFixed(2)}mm">${esc(qrCaption)}</div>
    </div>
  </div>
  <div class="barcode-row">
    <img src="${barcodeUrl}" style="max-height:${bcHeight.toFixed(2)}mm" />
    <div class="barcode-caption" style="font-size:${Math.max(3, baseFs - 1).toFixed(2)}pt">${esc(serialNo)}</div>
  </div>
</div>`;
  }

  /** The label's own dimensions — exactly as chosen, never re-oriented. */
  function resolvedSize() {
    const w = labelSize === 'custom' ? Math.max(10, Number(customW) || 60) : SIZES.find((s) => s.key === labelSize)!.w;
    const h = labelSize === 'custom' ? Math.max(10, Number(customH) || 40) : SIZES.find((s) => s.key === labelSize)!.h;
    // "2 labels" splits the chosen size into two equal labels side by side.
    const across = perRow;
    return { w: w / across, h, across, fullW: w };
  }

  function printCalibration() {
    const { w, h, across } = resolvedSize();
    openPrintWindow(buildCalibrationHtml({ w, h, across }), w, h);
  }

  async function printLabels() {
    if (!query.data) return;
    const { w, h, across } = resolvedSize();
    const padding = {
      top:    Number(offsetTop),
      right:  Number(offsetRight),
      bottom: Number(offsetBottom),
      left:   Number(offsetLeft),
    };

    const cells: string[] = [];
    for (const gi of items) {
      if (!selectedItemIds.has(gi.grnItemId)) continue;
      for (const s of gi.serials) {
        const barcodeUrl = genBarcodeSvgUrl(s.serialNo);
        const qrUrl = await QRCode.toDataURL(s.serialNo, {
          width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' },
        });
        cells.push(singleLabelHtml(w, h, s.serialNo, gi, barcodeUrl, qrUrl, s.qty ?? 1));
      }
    }
    if (cells.length === 0) return;

    // Per-GRN-cell classes (`.item-name`, `.qr-text`, etc.) that go alongside
    // the shared skeleton in labelPrint.ts.
    const extraCss = `
      .item-name { font-weight: normal; line-height: 1.25; overflow-wrap: break-word; word-break: break-word; }
      .item-cat  { color: #333; }
      .serial-no { font-weight: 900; margin-top: 1mm; overflow-wrap: break-word; word-break: break-all; line-height: 1.15; }
      .qr-text   { color: #555; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .barcode-caption { text-align: center; font-weight: 600; letter-spacing: 0.4px; margin-top: 0.4mm; overflow-wrap: break-word; word-break: break-all; line-height: 1.2; }
    `;
    const html = buildLabelPrintHtml({
      w, h, across, padding, extraCss,
      title: `GRN Labels — ${grnNo ?? ''}`,
      cellsHtml: cells.join(''),
    });
    openPrintWindow(html, w, h);
  }

  if (!open || grnId === null) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm">
      <div
        className="w-full max-w-4xl overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--modal-bg)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-3 bg-[var(--bg-card)]">
          <div>
            <div className="text-sm font-extrabold text-[var(--text-primary)]">GRN Labels {grnNo ? `— ${grnNo}` : ''}</div>
            <div className="text-[11px] text-[var(--text-muted)]">
              One QR + barcode label per received unit. Stick on the physical unit before warehouse scan.
            </div>
          </div>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100 bg-[var(--bg-soft)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        {query.isLoading ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">
            No serials found for this GRN. (GRNs created before this feature don't have auto-generated serials.)
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr]">
            {/* Left: items + serials list */}
            <div className="max-h-[70vh] overflow-y-auto border-b lg:border-b-0 lg:border-r border-[var(--border-color)] p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                  Items ({items.length}) — {totalSerials} of {query.data?.totalSerials ?? 0} labels selected
                </div>
                <div className="flex gap-1">
                  <button onClick={selectAll} className="text-[11px] font-bold text-accent hover:underline">All</button>
                  <span className="text-[var(--text-muted)]">·</span>
                  <button onClick={selectNone} className="text-[11px] font-bold text-[var(--text-secondary)] hover:underline">None</button>
                </div>
              </div>
              <div className="space-y-2">
                {items.map((gi) => {
                  const selected = selectedItemIds.has(gi.grnItemId);
                  return (
                    <label
                      key={gi.grnItemId}
                      className={
                        'flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 ' +
                        (selected
                          ? 'border-accent/50 bg-accent/[0.06]'
                          : 'border-[var(--border-color)] bg-[var(--bg-card)]')
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleItem(gi.grnItemId)}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold text-[var(--text-primary)]">{gi.item.name}</div>
                            <div className="text-[11px] font-mono text-[var(--text-muted)]">{gi.item.code}</div>
                          </div>
                          <div className="flex-shrink-0 text-xs font-bold text-accent">
                            {gi.serials.length} label{gi.serials.length === 1 ? '' : 's'}
                          </div>
                        </div>
                        {gi.serials.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                            {gi.serials.slice(0, 30).map((s) => (
                              <span
                                key={s.id}
                                className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${(s.qty ?? 1) > 1 ? 'bg-accent/15 font-bold text-accent' : 'bg-white/[0.06] text-[var(--text-secondary)]'}`}
                                title={(s.qty ?? 1) > 1 ? `Box of ${s.qty}` : undefined}
                              >
                                {s.serialNo}{(s.qty ?? 1) > 1 ? ` ×${s.qty}` : ''}
                              </span>
                            ))}
                            {gi.serials.length > 30 && (
                              <span className="text-[10px] italic text-[var(--text-muted)]">…and {gi.serials.length - 30} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Right: preview + size */}
            <div className="p-4">
              <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                Label Preview
              </div>
              {(() => {
                if (!previewSerial) return null;
                // Preview mirrors the print output — same aspect ratio, same
                // internal proportions, same offset padding. `PREVIEW_MM_TO_PX`
                // = 4 gives a comfortable on-screen size (60×40mm → 240×160px)
                // while everything else is scaled off `s` and mm-to-px so
                // dialing the offsets shows exactly what will print.
                const { w, h } = resolvedSize();
                const padL = Math.max(0, Math.min(15, Number(offsetLeft) || 0));
                const padT = Math.max(0, Math.min(15, Number(offsetTop) || 0));
                const padR = Math.max(0, Math.min(15, Number(offsetRight) || 0));
                const padB = Math.max(0, Math.min(15, Number(offsetBottom) || 0));
                const PX = 4;                          // 1 mm = 4 px on screen
                const s = Math.min(w / 60, h / 40);
                const baseFs = Math.max(3.5, 6 * s);
                const shortSide = Math.min(w, h);
                const qrSide = shortSide * 0.40;
                const bcHeight = shortSide * 0.28;
                // Portrait rolls (h > w) stack QR on top of info; landscape
                // rolls use info-left / QR-right. Mirrors buildLabelPrintHtml.
                const isPortrait = h > w;
                const info = (
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 * PX, overflow: 'hidden', textAlign: isPortrait ? 'center' : 'left', alignItems: isPortrait ? 'center' : 'stretch' }}>
                    <div style={{ fontSize: (baseFs + 1) * 1.33, lineHeight: 1.25, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{previewSerial.itemName}</div>
                    {previewSerial.category && <div style={{ fontSize: baseFs * 1.33, color: '#333' }}>{previewSerial.category}</div>}
                    <div style={{ fontSize: (baseFs + 4) * 1.33, fontWeight: 900, marginTop: 1 * PX, overflowWrap: 'break-word', wordBreak: 'break-all', lineHeight: 1.15 }}>{previewSerial.serialNo}</div>
                  </div>
                );
                const qr = (
                  <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 * PX }}>
                    {previewSerial.qrUrl
                      ? <img src={previewSerial.qrUrl} alt="QR" style={{ width: qrSide * PX, height: qrSide * PX }} />
                      : <div style={{ width: qrSide * PX, height: qrSide * PX, background: '#eee', borderRadius: 2 }} />
                    }
                  </div>
                );
                return (
                  <div
                    className="bg-white text-black font-sans shadow-lg mx-auto relative"
                    style={{
                      width: w * PX,
                      height: h * PX,
                      border: '1.5px solid #555',
                      boxSizing: 'border-box',
                      padding: `${padT * PX}px ${padR * PX}px ${padB * PX}px ${padL * PX}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1 * PX,
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: isPortrait ? 'column' : 'row', gap: 1.5 * PX, alignItems: isPortrait ? 'stretch' : 'flex-start', flex: 1, minHeight: 0 }}>
                      {isPortrait ? qr : info}
                      {isPortrait ? info : qr}
                    </div>
                    {previewSerial.barcodeUrl && (
                      <div style={{ flexShrink: 0, width: '100%' }}>
                        <img src={previewSerial.barcodeUrl} alt="barcode" style={{ width: '100%', display: 'block', maxHeight: bcHeight * PX }} />
                        <div style={{ fontSize: Math.max(3, baseFs - 1) * 1.33, textAlign: 'center', fontWeight: 600, letterSpacing: 0.4, marginTop: 0.4 * PX, overflowWrap: 'break-word', wordBreak: 'break-all', lineHeight: 1.2 }}>
                          {previewSerial.serialNo}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })() || (
                <div className="rounded-lg border border-dashed border-[var(--border-color)] p-6 text-center text-xs text-[var(--text-muted)]">
                  Select at least one item to preview a label.
                </div>
              )}

              <div className="mt-4">
                <div className="mb-1.5 text-[11px] font-bold text-[var(--text-secondary)]">Print Size</div>
                <div className="grid grid-cols-2 gap-2">
                  {SIZES.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setLabelSize(s.key)}
                      className="rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                      style={{
                        background: labelSize === s.key ? 'var(--accent)' : 'var(--bg-card)',
                        color: labelSize === s.key ? '#000' : 'var(--text-secondary)',
                        borderColor: labelSize === s.key ? 'var(--accent)' : 'var(--border-color)',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setLabelSize('custom')}
                    className="col-span-2 rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                    style={{
                      background: labelSize === 'custom' ? 'var(--accent)' : 'var(--bg-card)',
                      color: labelSize === 'custom' ? '#000' : 'var(--text-secondary)',
                      borderColor: labelSize === 'custom' ? 'var(--accent)' : 'var(--border-color)',
                    }}
                  >
                    Custom size
                  </button>
                </div>
                {labelSize === 'custom' && (
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" min="10" max="300" value={customW} onChange={(e) => setCustomW(e.target.value)}
                      className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] px-2 py-1 text-xs text-center text-[var(--text-primary)]"
                      placeholder="W" />
                    <span className="text-xs text-[var(--text-muted)]">mm ×</span>
                    <input type="number" min="10" max="300" value={customH} onChange={(e) => setCustomH(e.target.value)}
                      className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] px-2 py-1 text-xs text-center text-[var(--text-primary)]"
                      placeholder="H" />
                    <span className="text-xs text-[var(--text-muted)]">mm</span>
                  </div>
                )}
                <LabelsPerRowToggle value={perRow} onChange={setPerRow} w={resolvedSize().fullW} h={resolvedSize().h} />

                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-[10px] leading-relaxed text-amber-900">
                  <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> The printer driver's paper size must be exactly{' '}
                  <strong>{resolvedSize().fullW} × {resolvedSize().h} mm</strong> — set it once in
                  Printing Preferences → Page Setup (TSC: Stock → New), then pick that paper +
                  Margins: None + Scale: 100 in the print dialog. If the driver paper is bigger,
                  one label spills across multiple stickers.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--border-color)] px-5 py-3 bg-[var(--bg-card)]">
          <div className="text-[11px] text-[var(--text-muted)]">
            {totalSerials > 0 && (
              <>
                {totalSerials} label{totalSerials === 1 ? '' : 's'} @{' '}
                <span className="font-extrabold text-[var(--text-primary)]">
                  {resolvedSize().w} × {resolvedSize().h} mm
                </span>
                {' '}· {resolvedSize().across > 1 ? `same label on all ${resolvedSize().across} stickers of a row` : '1 label per sticker'}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="glass" size="sm" onClick={onClose}>Close</Button>
            <Button
              variant="warning"
              size="sm"
              onClick={printCalibration}
              title="Prints one sized rectangle with corner marks + mm rulers so you can hold it against a real sticker to verify the size"
            >
              <Ruler size={14} className="mr-1 inline-block align-[-2px]" /> Calibration
            </Button>
            <Button size="sm" onClick={printLabels} disabled={totalSerials === 0}>
              Print {totalSerials > 0 ? `(${totalSerials})` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
